import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as Happy } from '../../../assets/svg/GreenSmiley.svg'
import { ReactComponent as Sad } from '../../../assets/svg/RedSad.svg'
import InfoButton from '../../common/InfoButton'
import { classData } from '../../../constants/ClassAnalyticsConstants'
import { LoadingContainer } from '../student/CheckInWidget'
import { loadCLassWellbeingSchoolYearData } from '../../../server/server'

function ClassCardAnalytics({ classId }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState(classData)
    const showLoading = useMemo(() => loading || !data, [loading, data])

    useEffect(() => {
        setLoading(true)
        loadData()
    }, [classId])

    const loadData = async () => {
        const resp = await loadCLassWellbeingSchoolYearData(classId)
        if (resp.status === 200) {
            setData(resp.data)
        } else {
            setData(null)
        }
        setLoading(false)
    }

    return (
        <div className="mt-[10px]  pb-8 px-8 ">
            <div className="relative">
                <DataContainer data={data} blur={showLoading} />
                {showLoading && <LoadingContainer showEmptyData={!loading && !data} />}
            </div>
        </div>
    )
}

export default ClassCardAnalytics

const DataContainer = ({ data, blur }) => {
    const { t } = useTranslation()

    const comfortPercentage = useMemo(() => Math.round(data?.comfortablePercentage * 100), [data?.comfortablePercentage])

    return (
        <div className={blur ? 'blur' : ''} data-tour="class_emotional_info">
            <div className="flex flex-row items-center mt-6">
                <div className="flex gap-[20px] flex-col w-full">
                    <div className="text-darkenPurple text-[16px] leading-5 font-semibold flex items-center justify-between w-full">
                        {t('Class wellbeing for this school year')}
                        <InfoButton
                            text={t('Shows the total emotions logged by the class so far this year, with a simple breakdown into comfortable and uncomfortable emotions')}
                        />
                    </div>
                    <div className="flex flex-col gap-[10px]">
                        <span className="text-mediumGray text-[16px] leading-5 font-medium flex flex-row gap-1">
                            {t('TotalEmotions')}: <p className="text-darkenPurple text-[16px] leading-5 font-bold">{data?.total}</p>
                        </span>

                        <div className="flex flex-row items-center gap-2">
                            <Happy height={32} width={32} />
                            <span className="text-mediumGray text-[16px] leading-5 font-medium flex flex-row gap-1">
                                <p className="text-darkenPurple text-[16px] leading-5 font-bold">{data?.comfortable}</p>
                                <div className="flex flex-row gap-2">
                                    <p>{t('Comfortable Emotions')}</p>({comfortPercentage}%)
                                </div>
                            </span>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                            <Sad height={32} width={32} />
                            <span className="text-mediumGray text-[16px] leading-5 font-medium flex flex-row gap-1">
                                <p className="text-darkenPurple text-[16px] leading-5 font-bold">{data?.uncomfortable}</p>
                                <div className="flex flex-row gap-2">
                                    <p>{t('Uncomfortable Emotions')}</p>({100 - comfortPercentage}%)
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
